/**
 * ContractList Component
 * 
 * Displays a list of contracts with status, expiration indicators, and filtering.
 */

import { t, getCurrentLanguage, getCurrency } from '../../i18n/translations.js';
import { escapeHtml } from '../../utils/sanitize.js';

/**
 * Contract status configurations
 */
export const STATUS_CONFIG = {
  draft: {
    label: 'status.draft',
    color: 'var(--gray-600)',
    bgColor: 'var(--gray-100)',
    icon: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
    </svg>`,
  },
  pending_review: {
    label: 'status.pendingReview',
    color: 'var(--warning)',
    bgColor: 'var(--warning-light)',
    icon: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
      <circle cx="12" cy="12" r="10"/>
      <polyline points="12 6 12 12 16 14"/>
    </svg>`,
  },
  pending_approval: {
    label: 'status.pendingApproval',
    color: 'var(--info)',
    bgColor: 'var(--info-light)',
    icon: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
      <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
      <circle cx="8.5" cy="7" r="4"/>
      <polyline points="17 11 19 13 23 9"/>
    </svg>`,
  },
  approved: {
    label: 'status.approved',
    color: 'var(--success)',
    bgColor: 'var(--success-light)',
    icon: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
      <polyline points="22 4 12 14.01 9 11.01"/>
    </svg>`,
  },
  active: {
    label: 'status.active',
    color: 'var(--success)',
    bgColor: 'var(--success-light)',
    icon: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
      <polyline points="20 6 9 17 4 12"/>
    </svg>`,
  },
  expired: {
    label: 'status.expired',
    color: 'var(--error)',
    bgColor: 'var(--error-light)',
    icon: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
      <circle cx="12" cy="12" r="10"/>
      <line x1="15" y1="9" x2="9" y2="15"/>
      <line x1="9" y1="9" x2="15" y2="15"/>
    </svg>`,
  },
  terminated: {
    label: 'status.terminated',
    color: 'var(--gray-600)',
    bgColor: 'var(--gray-200)',
    icon: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
      <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
      <line x1="9" y1="9" x2="15" y2="15"/>
      <line x1="15" y1="9" x2="9" y2="15"/>
    </svg>`,
  },
  renewed: {
    label: 'status.renewed',
    color: 'var(--info)',
    bgColor: 'var(--info-light)',
    icon: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
      <polyline points="23 4 23 10 17 10"/>
      <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/>
    </svg>`,
  },
};

/**
 * Status transition labels for action menu
 */
const STATUS_ACTIONS = {
  pending_review: { label: 'Submit for Review', icon: '📤' },
  pending_approval: { label: 'Submit for Approval', icon: '📋' },
  approved: { label: 'Approve', icon: '✅' },
  active: { label: 'Activate', icon: '▶️' },
  terminated: { label: 'Terminate', icon: '🛑' },
  renewed: { label: 'Mark as Renewed', icon: '🔄' },
  draft: { label: 'Return to Draft', icon: '↩️' },
};

// Map non-standard currency codes to ISO 4217 codes
const CURRENCY_CODE_MAP = {
  'DT': 'TND',   // Tunisian Dinar
  'DA': 'DZD',   // Algerian Dinar
  'DH': 'MAD',   // Moroccan Dirham
  'LE': 'EGP',   // Egyptian Pound
  'SR': 'SAR',   // Saudi Riyal
};

/**
 * Format currency (uses user's preferred currency as default)
 */
function formatCurrency(amount, currency = null) {
  const lang = getCurrentLanguage();
  const locale = lang === 'ar' ? 'ar-SA' : lang === 'fr' ? 'fr-FR' : 'en-US';
  let currencyCode = currency || getCurrency();
  // Map non-standard codes to ISO 4217
  currencyCode = CURRENCY_CODE_MAP[currencyCode] || currencyCode;
  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currencyCode,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  } catch {
    // Fallback if currency code is still invalid
    return `${currencyCode} ${new Intl.NumberFormat(locale).format(amount)}`;
  }
}

/**
 * Format date
 */
function formatDate(dateStr) {
  if (!dateStr) return '-';
  const lang = getCurrentLanguage();
  const locale = lang === 'ar' ? 'ar-SA' : lang === 'fr' ? 'fr-FR' : 'en-US';
  return new Date(dateStr).toLocaleDateString(locale, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

/**
 * Calculate days until expiration
 */
function getDaysUntilExpiry(expirationDate) {
  if (!expirationDate) return null;
  const expiry = new Date(expirationDate);
  const today = new Date();
  const diffTime = expiry - today;
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
}

/**
 * Get expiration urgency class
 */
function getExpirationUrgency(daysUntilExpiry) {
  if (daysUntilExpiry === null) return '';
  if (daysUntilExpiry <= 0) return 'expired';
  if (daysUntilExpiry <= 30) return 'critical';
  if (daysUntilExpiry <= 60) return 'warning';
  if (daysUntilExpiry <= 90) return 'info';
  return '';
}

/**
 * Render status badge
 */
function renderStatusBadge(status) {
  const config = STATUS_CONFIG[status] || STATUS_CONFIG.draft;
  return `
    <span class="status-badge" style="color: ${config.color}; background: ${config.bgColor}">
      ${config.icon}
      <span>${t(config.label) || status}</span>
    </span>
  `;
}

/**
 * Render expiration indicator
 */
function renderExpirationIndicator(expirationDate, status) {
  if (status !== 'active') return '';
  
  const daysUntil = getDaysUntilExpiry(expirationDate);
  if (daysUntil === null || daysUntil > 90) return '';
  
  const urgency = getExpirationUrgency(daysUntil);
  
  let message;
  if (daysUntil <= 0) {
    message = t('contract.expired') || 'Expired';
  } else if (daysUntil === 1) {
    message = t('contract.expiresTomorrow') || 'Expires tomorrow';
  } else if (daysUntil <= 30) {
    message = `${daysUntil} ${t('contract.daysLeft') || 'days left'}`;
  } else {
    message = `${daysUntil} ${t('contract.daysLeft') || 'days'}`;
  }
  
  return `
    <span class="expiration-indicator ${urgency}">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <circle cx="12" cy="12" r="10"/>
        <polyline points="12 6 12 12 16 14"/>
      </svg>
      <span>${message}</span>
    </span>
  `;
}

/**
 * Render renewal type badge
 */
function renderRenewalBadge(renewalType) {
  if (!renewalType) return '';
  
  const labels = {
    auto_renewal: { label: 'contract.autoRenewal', icon: '🔄' },
    manual_renewal: { label: 'contract.manualRenewal', icon: '📝' },
    one_time: { label: 'contract.oneTime', icon: '1️⃣' },
  };
  
  const config = labels[renewalType];
  if (!config) return '';
  
  return `
    <span class="renewal-badge" title="${t(config.label) || renewalType}">
      ${config.icon}
    </span>
  `;
}

/**
 * Render action menu dropdown
 */
function renderActionMenu(contract) {
  const status = contract.status;

  // Define available transitions based on current status
  const transitions = {
    draft: ['pending_review', 'terminated'],
    pending_review: ['draft', 'pending_approval', 'terminated'],
    pending_approval: ['pending_review', 'approved', 'terminated'],
    approved: ['active', 'terminated'],
    active: ['expired', 'terminated', 'renewed'],
    expired: ['renewed'],
    terminated: [],
    renewed: [],
  };

  const availableTransitions = transitions[status] || [];

  return `
    <div class="action-menu" data-contract-id="${contract.id}">
      <div class="action-menu-content">
        <button class="action-menu-item" onclick="window.viewContract('${contract.id}')">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
            <circle cx="12" cy="12" r="3"/>
          </svg>
          <span>View Details</span>
        </button>
        <button class="action-menu-item" onclick="window.editContract('${contract.id}')">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
          </svg>
          <span>Edit</span>
        </button>
        <button class="action-menu-item" onclick="window.downloadContract('${contract.id}')">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
            <polyline points="7 10 12 15 17 10"/>
            <line x1="12" y1="15" x2="12" y2="3"/>
          </svg>
          <span>Download</span>
        </button>

        ${availableTransitions.length > 0 ? `
          <div class="action-menu-divider"></div>
          <div class="action-menu-section-label">Change Status</div>
          ${availableTransitions.map(newStatus => {
            const action = STATUS_ACTIONS[newStatus];
            return `
              <button class="action-menu-item" onclick="window.transitionContract('${contract.id}', '${newStatus}')">
                <span class="action-icon">${action?.icon || '•'}</span>
                <span>${action?.label || newStatus}</span>
              </button>
            `;
          }).join('')}
        ` : ''}

        ${status !== 'terminated' && status !== 'renewed' ? `
          <div class="action-menu-divider"></div>
          <button class="action-menu-item action-menu-item-danger" onclick="window.deleteContract('${contract.id}')">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <polyline points="3 6 5 6 21 6"/>
              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
            </svg>
            <span>Delete</span>
          </button>
        ` : ''}
      </div>
    </div>
  `;
}

/**
 * Render a single contract card
 */
function renderContractCard(contract) {
  const daysUntil = getDaysUntilExpiry(contract.expirationDate);
  const urgency = getExpirationUrgency(daysUntil);
  
  return `
    <div class="contract-card ${urgency}" data-contract-id="${contract.id}">
      <div class="contract-card-header">
        <div class="contract-title-row">
          <h3 class="contract-title">
            <a href="/#/contracts/${contract.id}">${escapeHtml(contract.title)}</a>
          </h3>
          ${renderRenewalBadge(contract.renewalType)}
        </div>
        <span class="contract-number">${escapeHtml(contract.contractNumber || '-')}</span>
      </div>
      
      <div class="contract-card-body">
        <div class="contract-info-row">
          <span class="label">${t('contract.vendor') || 'Vendor'}</span>
          <span class="value">${escapeHtml(contract.vendorName || '-')}</span>
        </div>
        <div class="contract-info-row">
          <span class="label">${t('contract.value') || 'Value'}</span>
          <span class="value">${formatCurrency(contract.totalValue, contract.currency)}</span>
        </div>
        <div class="contract-info-row">
          <span class="label">${t('contract.period') || 'Period'}</span>
          <span class="value">${formatDate(contract.effectiveDate)} - ${formatDate(contract.expirationDate)}</span>
        </div>
      </div>
      
      <div class="contract-card-footer">
        ${renderStatusBadge(contract.status)}
        ${renderExpirationIndicator(contract.expirationDate, contract.status)}
      </div>
      
      <div class="contract-card-actions">
        <button class="btn-icon" onclick="window.viewContract('${contract.id}')" title="${t('common.view') || 'View'}">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
            <circle cx="12" cy="12" r="3"/>
          </svg>
        </button>
        ${contract.status === 'active' ? `
          <button class="btn-icon" onclick="window.renewContract('${contract.id}')" title="${t('contract.renew') || 'Renew'}">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <polyline points="23 4 23 10 17 10"/>
              <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/>
            </svg>
          </button>
        ` : ''}
        <div class="action-menu-wrapper">
          <button class="btn-icon action-menu-trigger" onclick="window.toggleContractActions(event, '${contract.id}')" title="${t('common.more') || 'More'}">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <circle cx="12" cy="12" r="1"/>
              <circle cx="19" cy="12" r="1"/>
              <circle cx="5" cy="12" r="1"/>
            </svg>
          </button>
          ${renderActionMenu(contract)}
        </div>
      </div>
    </div>
  `;
}

/**
 * Render a single contract row (table view)
 */
function renderContractRow(contract) {
  const daysUntil = getDaysUntilExpiry(contract.expirationDate);
  const urgency = getExpirationUrgency(daysUntil);
  
  return `
    <tr class="contract-row ${urgency}" data-contract-id="${contract.id}">
      <td class="cell-checkbox">
        <input type="checkbox" class="contract-checkbox" value="${contract.id}" />
      </td>
      <td class="cell-title">
        <div class="contract-title-cell">
          <a href="/#/contracts/${contract.id}" class="contract-link">${escapeHtml(contract.title)}</a>
          <span class="contract-number">${escapeHtml(contract.contractNumber || '')}</span>
        </div>
      </td>
      <td class="cell-vendor">${escapeHtml(contract.vendorName || '-')}</td>
      <td class="cell-value">${formatCurrency(contract.totalValue, contract.currency)}</td>
      <td class="cell-expiration">
        <span>${formatDate(contract.expirationDate)}</span>
        ${renderExpirationIndicator(contract.expirationDate, contract.status)}
      </td>
      <td class="cell-status">${renderStatusBadge(contract.status)}</td>
      <td class="cell-actions">
        <div class="action-buttons">
          <button class="btn-icon" onclick="window.viewContract('${contract.id}')" title="${t('common.view') || 'View'}">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
              <circle cx="12" cy="12" r="3"/>
            </svg>
          </button>
          <div class="action-menu-wrapper">
            <button class="btn-icon action-menu-trigger" onclick="window.toggleContractActions(event, '${contract.id}')" title="${t('common.more') || 'More'}">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <circle cx="12" cy="12" r="1"/>
                <circle cx="19" cy="12" r="1"/>
                <circle cx="5" cy="12" r="1"/>
              </svg>
            </button>
            ${renderActionMenu(contract)}
          </div>
        </div>
      </td>
    </tr>
  `;
}

/**
 * Render contract filters
 */
function renderFilters(activeFilters = {}) {
  return `
    <div class="contract-filters">
      <div class="filter-group">
        <label>${t('contract.status') || 'Status'}</label>
        <select id="filter-status" class="filter-select" onchange="window.applyContractFilters()">
          <option value="">${t('common.all') || 'All'}</option>
          <option value="draft" ${activeFilters.status === 'draft' ? 'selected' : ''}>${t('status.draft') || 'Draft'}</option>
          <option value="pending_review" ${activeFilters.status === 'pending_review' ? 'selected' : ''}>${t('status.pendingReview') || 'Pending Review'}</option>
          <option value="pending_approval" ${activeFilters.status === 'pending_approval' ? 'selected' : ''}>${t('status.pendingApproval') || 'Pending Approval'}</option>
          <option value="approved" ${activeFilters.status === 'approved' ? 'selected' : ''}>${t('status.approved') || 'Approved'}</option>
          <option value="active" ${activeFilters.status === 'active' ? 'selected' : ''}>${t('status.active') || 'Active'}</option>
          <option value="expired" ${activeFilters.status === 'expired' ? 'selected' : ''}>${t('status.expired') || 'Expired'}</option>
          <option value="terminated" ${activeFilters.status === 'terminated' ? 'selected' : ''}>${t('status.terminated') || 'Terminated'}</option>
          <option value="renewed" ${activeFilters.status === 'renewed' ? 'selected' : ''}>${t('status.renewed') || 'Renewed'}</option>
        </select>
      </div>
      <div class="filter-group">
        <label>${t('contract.expiringWithin') || 'Expiring Within'}</label>
        <select id="filter-expiring" class="filter-select" onchange="window.applyContractFilters()">
          <option value="">${t('common.any') || 'Any'}</option>
          <option value="30" ${activeFilters.expiringDays === '30' ? 'selected' : ''}>30 ${t('common.days') || 'days'}</option>
          <option value="60" ${activeFilters.expiringDays === '60' ? 'selected' : ''}>60 ${t('common.days') || 'days'}</option>
          <option value="90" ${activeFilters.expiringDays === '90' ? 'selected' : ''}>90 ${t('common.days') || 'days'}</option>
        </select>
      </div>
      <div class="filter-group">
        <label>${t('contract.vendor') || 'Vendor'}</label>
        <input type="text" id="filter-vendor" class="filter-input" placeholder="${t('common.search') || 'Search'}..." value="${activeFilters.vendor || ''}" onchange="window.applyContractFilters()" />
      </div>
      <div class="filter-group filter-actions">
        <button class="btn btn-secondary btn-sm" onclick="window.clearContractFilters()">
          ${t('common.clear') || 'Clear'}
        </button>
      </div>
    </div>
  `;
}

/**
 * Render view toggle
 */
function renderViewToggle(activeView = 'card') {
  return `
    <div class="view-toggle">
      <button class="view-btn ${activeView === 'card' ? 'active' : ''}" onclick="window.setContractView('card')" title="${t('common.cardView') || 'Card View'}">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <rect x="3" y="3" width="7" height="7"/>
          <rect x="14" y="3" width="7" height="7"/>
          <rect x="14" y="14" width="7" height="7"/>
          <rect x="3" y="14" width="7" height="7"/>
        </svg>
      </button>
      <button class="view-btn ${activeView === 'table' ? 'active' : ''}" onclick="window.setContractView('table')" title="${t('common.tableView') || 'Table View'}">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <line x1="8" y1="6" x2="21" y2="6"/>
          <line x1="8" y1="12" x2="21" y2="12"/>
          <line x1="8" y1="18" x2="21" y2="18"/>
          <line x1="3" y1="6" x2="3.01" y2="6"/>
          <line x1="3" y1="12" x2="3.01" y2="12"/>
          <line x1="3" y1="18" x2="3.01" y2="18"/>
        </svg>
      </button>
    </div>
  `;
}

/**
 * Render empty state
 */
function renderEmptyState() {
  return `
    <div class="empty-state">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="empty-icon">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
        <polyline points="14 2 14 8 20 8"/>
      </svg>
      <h3>${t('contract.noContracts') || 'No contracts found'}</h3>
      <p>${t('contract.noContractsDescription') || 'Upload a contract to get started'}</p>
      <a href="#upload?type=contract" class="btn btn-primary">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
          <polyline points="17 8 12 3 7 8"/>
          <line x1="12" y1="3" x2="12" y2="15"/>
        </svg>
        ${t('contract.uploadContract') || 'Upload Contract'}
      </a>
    </div>
  `;
}

/**
 * Render pagination
 */
function renderPagination(pagination) {
  const { page, totalPages, totalItems } = pagination;
  
  if (totalPages <= 1) return '';
  
  const pages = [];
  const maxVisible = 5;
  let start = Math.max(1, page - Math.floor(maxVisible / 2));
  let end = Math.min(totalPages, start + maxVisible - 1);
  
  if (end - start < maxVisible - 1) {
    start = Math.max(1, end - maxVisible + 1);
  }
  
  for (let i = start; i <= end; i++) {
    pages.push(i);
  }
  
  return `
    <div class="pagination">
      <span class="pagination-info">
        ${t('common.showing') || 'Showing'} ${(page - 1) * 20 + 1} - ${Math.min(page * 20, totalItems)} ${t('common.of') || 'of'} ${totalItems}
      </span>
      <div class="pagination-buttons">
        <button class="btn-page" ${page === 1 ? 'disabled' : ''} onclick="window.goToContractPage(${page - 1})">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <polyline points="15 18 9 12 15 6"/>
          </svg>
        </button>
        ${pages.map(p => `
          <button class="btn-page ${p === page ? 'active' : ''}" onclick="window.goToContractPage(${p})">${p}</button>
        `).join('')}
        <button class="btn-page" ${page === totalPages ? 'disabled' : ''} onclick="window.goToContractPage(${page + 1})">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <polyline points="9 18 15 12 9 6"/>
          </svg>
        </button>
      </div>
    </div>
  `;
}

/**
 * Render the full contract list component
 */
export function renderContractList(options = {}) {
  const {
    contracts = [],
    filters = {},
    pagination = { page: 1, totalPages: 1, totalItems: 0 },
    view = 'card',
    showFilters = true,
  } = options;

  if (contracts.length === 0 && !showFilters) {
    return renderEmptyState();
  }

  return `
    <div class="contract-list-container">
      <div class="contract-list-header">
        <div class="header-left">
          <h2>${t('nav.contracts') || 'Contracts'}</h2>
          <span class="item-count">${pagination.totalItems} ${t('common.items') || 'items'}</span>
        </div>
        <div class="header-right">
          ${renderViewToggle(view)}
          <a href="#upload?type=contract" class="btn btn-primary">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <line x1="12" y1="5" x2="12" y2="19"/>
              <line x1="5" y1="12" x2="19" y2="12"/>
            </svg>
            ${t('contract.uploadNew') || 'Upload Contract'}
          </a>
        </div>
      </div>

      ${showFilters ? renderFilters(filters) : ''}

      ${contracts.length === 0 ? renderEmptyState() : (
        view === 'card' ? `
          <div class="contract-grid">
            ${contracts.map(contract => renderContractCard(contract)).join('')}
          </div>
        ` : `
          <div class="table-container">
            <table class="contract-table">
              <thead>
                <tr>
                  <th class="th-checkbox">
                    <input type="checkbox" id="select-all-contracts" onchange="window.toggleAllContracts(this.checked)" />
                  </th>
                  <th class="th-sortable" data-sort="title">${t('contract.title') || 'Title'}</th>
                  <th class="th-sortable" data-sort="vendorName">${t('contract.vendor') || 'Vendor'}</th>
                  <th class="th-sortable" data-sort="totalValue">${t('contract.value') || 'Value'}</th>
                  <th class="th-sortable" data-sort="expirationDate">${t('contract.expires') || 'Expires'}</th>
                  <th>${t('contract.status') || 'Status'}</th>
                  <th>${t('common.actions') || 'Actions'}</th>
                </tr>
              </thead>
              <tbody>
                ${contracts.map(contract => renderContractRow(contract)).join('')}
              </tbody>
            </table>
          </div>
        `
      )}
      
      ${renderPagination(pagination)}
    </div>
  `;
}

/**
 * Initialize contract list event handlers
 */
export function initContractListHandlers() {
  window.toggleAllContracts = (checked) => {
    const checkboxes = document.querySelectorAll('.contract-checkbox');
    checkboxes.forEach(cb => cb.checked = checked);
  };
}

export { formatCurrency, formatDate, getDaysUntilExpiry };
